/**
 * QA Issues Panel
 * Displays quality assurance check results
 * 
 * Location: src/components/cat/QAIssuesPanel.tsx
 */

import React from 'react'
import { X, AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react'
import Button from '@/components/common/Button'
import { type QAIssue, QAChecker } from '@/components/cat/qaChecker'

interface QAIssuesPanelProps {
  isOpen: boolean
  onClose: () => void
  issues: QAIssue[]
  segmentNumber: number
}

const QAIssuesPanel: React.FC<QAIssuesPanelProps> = ({
  isOpen,
  onClose,
  issues,
  segmentNumber
}) => {
  if (!isOpen) return null

  const getSeverityIcon = (severity: QAIssue['severity']) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-600" />
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-gold-600" />
      case 'info':
        return <Info className="h-5 w-5 text-blue-600" />
      default:
        return <Info className="h-5 w-5 text-gray-800" />
    }
  }

  const getSeverityBadge = (severity: QAIssue['severity']) => {
    const badges = {
      error: 'bg-red-100 text-red-800 border-red-200',
      warning: 'bg-gold-100 text-gold-800 border-gold-200',
      info: 'bg-blue-100 text-blue-800 border-blue-200'
    }
    return badges[severity] || badges.info
  }

  const errorCount = issues.filter(i => i.severity === 'error').length
  const warningCount = issues.filter(i => i.severity === 'warning').length
  const infoCount = issues.filter(i => i.severity === 'info').length

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-t-lg sm:rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {issues.length === 0 ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-gold-600" />
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                QA Check Results
              </h3>
              <p className="text-sm text-gray-700">
                Segment #{segmentNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-700" />
          </button>
        </div>

        {/* Summary */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4 text-sm">
            {issues.length === 0 ? (
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span className="font-medium">No issues found</span>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-800">Total issues:</span>
                  <span className="font-semibold text-gray-900">{issues.length}</span>
                </div>
                {errorCount > 0 && (
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="font-semibold text-red-600">{errorCount} error{errorCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {warningCount > 0 && (
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-gold-600" />
                    <span className="font-semibold text-gold-600">{warningCount} warning{warningCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {infoCount > 0 && (
                  <div className="flex items-center space-x-2">
                    <Info className="h-4 w-4 text-blue-600" />
                    <span className="font-semibold text-blue-600">{infoCount} info</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Issues List */}
        <div className="flex-1 overflow-y-auto p-4">
          {issues.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">All Clear!</h4>
              <p className="text-gray-800">
                No quality issues detected in this segment.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {issues.map((issue, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 ${QAChecker.getSeverityColor(issue.severity)}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getSeverityIcon(issue.severity)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium">
                          {issue.message}
                        </h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSeverityBadge(issue.severity)}`}>
                          {issue.severity.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm opacity-90 mb-2">
                        {QAChecker.getIssueTypeName(issue.type)}
                      </p>
                      {issue.details && (
                        <div className="text-sm opacity-75 bg-white bg-opacity-50 rounded p-2 mt-2">
                          {issue.details}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-800">
              <p>💡 <strong>Tip:</strong> Press <kbd className="px-2 py-1 bg-gray-200 rounded text-xs">Ctrl+Q</kbd> to run QA check</p>
            </div>
            <Button
              variant="primary"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QAIssuesPanel